import { useCallback, useMemo, useState } from 'react';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { useUserTokenDecoded } from '../../../../redux/slices/appInfo/hooks';
import { newestAutoAdded } from './autoAdded';

function storageKey(userId: number, smartLinkId: number): string {
  return `m24.smartlinkEditor.autoAddNote.${userId}.${smartLinkId}`;
}

function readDismissed(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * The note after a Bio Link auto add (design e3): it shows once after each auto add. A close keeps
 * the time of that add in the browser storage, per user and page, so the next auto add shows again.
 */
export function useAutoAddNote(smartLinkId: number, widgets: readonly SerializedWidget[]) {
  const user = useUserTokenDecoded();
  const key = storageKey(user?.id ?? 0, smartLinkId);
  const newest = useMemo(() => newestAutoAdded(widgets), [widgets]);
  const [dismissedAt, setDismissedAt] = useState<string | null>(() => readDismissed(key));

  const dismiss = useCallback(() => {
    if (newest === null) {
      return;
    }
    setDismissedAt(newest.at);
    try {
      window.localStorage.setItem(key, newest.at);
    } catch {
      // A private window or blocked storage: the note goes away for this visit only.
    }
  }, [key, newest]);

  const isVisible = newest !== null && dismissedAt !== null ? newest.at > dismissedAt : newest !== null;
  return { newest: isVisible ? newest : null, dismiss };
}
